import { Fragment, useCallback, useRef, useId, useEffect } from 'react';
import cc from 'classnames';
import { observer } from 'decorators';
import { makeStyles, useAction, useAutoFocus } from 'hooks';
import { Grid, Paper, Typography, ButtonBase, IconButton, Tooltip, Collapse, List, ListItem, ListItemText } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import TimezonePicker from 'components/timezonePicker';
import Spinner from 'components/spinner';
import Button from 'components/button';
import { shortTime } from 'utils/date';
import media from 'services/media';

import { ChevronLeft, ChevronRight, Alert, WeatherSunny, ClockOutline, WeatherNight } from 'mdi-material-ui';

export default observer(function TimePickStep({ store, isFirst }) {
  const headerId = useId();

  if (!store) { return null; }

  return <Grid container spacing={2} direction="column">
    <CalendarHeader store={store} headerId={headerId} isFirst={isFirst} />
    <Grid item>
      <Calendar store={store} headerId={headerId} />
    </Grid>
    <CalendarTimes store={store} />
  </Grid>;
});

const useCalendarHeaderStyles = makeStyles(theme => ({
  header: {
    textAlign: 'center'
  },
  headerText: {
    display: 'inline-block',
    width: theme.spacing(17),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(25)
    }
  },
  middleAlign: {
    verticalAlign: 'middle'
  }
}));

const CalendarHeader = observer(function CalendarHeader({ store, headerId, isFirst }) {
  const classes = useCalendarHeaderStyles();

  const { month, canNext, canPrevious, timezone, settings, internalMode } = store;
  const isSmall = media.is('xs');

  const handleTimezone = useAction(tz => (store.timezone = tz), [ store ]);
  const handlePrev = useCallback(() => store.moveMonth(-1), [ store ]);
  const handleNext = useCallback(() => store.moveMonth(1), [ store ]);

  const ref = useRef(null);
  useAutoFocus(ref, { enabled: () => !isFirst && !store.focusedDay, deps: [ isFirst ] });

  return <Grid ref={ref} item className={classes.header}>
    <strong className={classes.middleAlign}>Timezone:</strong>&nbsp;&nbsp;
    <TimezonePicker
      notEmpty
      detectDefault={!internalMode}
      disableUnderline
      countryCode={settings.countryCode}
      culture={settings.culture}
      value={timezone}
      onChange={handleTimezone}
      className={classes.middleAlign}
    />
    <br />
    <Grid container alignItems="center" justifyContent="center" id={headerId}>
      <Grid item>
        <IconButtonWithDisabledLabel title="Cannot book earlier" disabled={!canPrevious} onClick={handlePrev} aria-label="Previous Month">
          <ChevronLeft fontSize="large" />
        </IconButtonWithDisabledLabel>
      </Grid>
      <Grid item>
        <Typography variant={isSmall ? 'h5' : 'h4'} className={classes.headerText}>{month && month.format('MMMM')}</Typography>
      </Grid>
      <Grid item>
        <IconButtonWithDisabledLabel title="Cannot book later" disabled={!canNext} onClick={handleNext} aria-label="Next Month">
          <ChevronRight fontSize="large" />
        </IconButtonWithDisabledLabel>
      </Grid>
    </Grid>
  </Grid>;
});

const IconButtonWithDisabledLabel = observer(function IconButtonWithDisabledLabel({ title, disabled, ...other }) {
  const inner = <IconButton disabled={disabled} {...other} />;
  if (disabled) {
    return <Tooltip title={title}>
      <span>
        { inner }
      </span>
    </Tooltip>;
  }
  return inner;
});

const useCalendarStyles = makeStyles(theme => ({
  dayHeader: {
    textAlign: 'center',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    padding: theme.spacing(1)
  },
  day: {
    width: '100%',
    height: '100%',
    position: 'relative',
    border: `2px solid ${theme.palette.grey[200]}`,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(11)
    }
  },
  dayNumber: {
    position: 'absolute',
    left: theme.spacing(1),
    top: theme.spacing(1)
  },
  notInMonth: {
    position: 'relative',
    '&:after': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: alpha(theme.palette.background.default, 0.7),
      pointerEvents: 'none'
    }
  },
  selectedDay: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.getContrastText(theme.palette.primary.light),
    borderColor: theme.palette.primary.main
  },
  noData: {
    backgroundColor: theme.palette.grey[200]
  },
  noTimes: {
    color: theme.palette.grey[200]
  },
  spinnerContainer: {
    position: 'relative'
  },
  spinnerOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.54)'
  },
  warningOverlay: {
    position: 'absolute',
    left: `calc(50% - ${theme.spacing(20)}px)`,
    width: theme.spacing(40),
    top: '20%',
    padding: theme.spacing(2),
    textAlign: 'center'
  },
  noPad: {
    padding: '0 !important'
  }
}));

const Calendar = observer(function Calendar({ store, headerId }) {
  const classes = useCalendarStyles();
  const { headers, calendarData, month, selectedDay, focusedDay, isLoading, hasNoCalendarData } = store;

  const tp = useRef(null);
  const clearT = useCallback(() => { if (tp.current) { clearTimeout(tp.current); tp.current = null; } }, []);
  useEffect(() => () => clearT(), [ clearT ]);

  const handleFocus = useCallback(() => {
    clearT();
    if (!focusedDay) { store.startFocus(); }
  }, [ focusedDay, store, clearT ]);

  const handleBlur = useCallback(() => {
    tp.current = setTimeout(() => store.lostFocus(), 30);
  }, [ store ]);

  const handleKeyDown = useCallback(e => store.keyPress(e), [ store ]);

  const focusedDayRef = useRef();
  useEffect(() => {
    if (focusedDay) {
      focusedDayRef.current?.focusVisible();
    }
  }, [ focusedDay ]);

  if (!calendarData.length) {
    return <Spinner text="Loading..." />;
  }

  const isSmall = media.is('xs');
  return <Grid
    container
    direction="column"
    spacing={1}
    className={classes.spinnerContainer}
    role="grid"
    aria-labelledby={headerId}
    onFocus={handleFocus}
    onBlur={handleBlur}
    onKeyDown={handleKeyDown}
  >
    <Grid item role="rowgroup">
      <Grid container spacing={1} role="row">
        { headers.map(h => (
          <Grid role="columnheader" item xs key={h}>
            <div className={classes.dayHeader}>{isSmall ? h[0] : h}</div>
          </Grid>
        ))}
      </Grid>
    </Grid>
    { calendarData.map(wk => {
      const isSelectedWeek = !!(selectedDay && wk.dayData.find(d => d.date.isSame(selectedDay.date)));

      return <Grid item key={wk.week} className={cc(selectedDay && !isSelectedWeek && classes.noPad)}>
        <Collapse in={!selectedDay || isSelectedWeek}>
          <Grid role="row" container spacing={1} alignItems="stretch">
            { wk.dayData.map(d => {
              const isSelected = selectedDay && d.date.isSame(selectedDay.date);
              const isFocused = focusedDay && d.date.isSame(focusedDay);
              const handleSelect = () => store.selectDay(d);
              const isPrimaryOption = isFocused || (!focusedDay && isSelected) || (!focusedDay && !selectedDay && d.firstFocus);

              return <Grid
                item xs
                key={d.date.format()}
                className={cc(d.date.month() !== month.month() && classes.notInMonth)}
                role="gridcell"
              >
                <ButtonBase
                  action={isPrimaryOption ? focusedDayRef : undefined}
                  focusRipple
                  onClick={handleSelect}
                  disabled={!d.data}
                  className={cc(
                    classes.day,
                    !d.data && classes.noData,
                    isSelected && classes.selectedDay
                  )}
                  tabIndex={isPrimaryOption ? 0 : -1}
                >
                  <Typography variant="subtitle1" className={classes.dayNumber}>
                    { d.date.date() }
                  </Typography>
                  { d.data &&
                    <Grid container>
                      <Grid item xs={12} sm>
                        <WeatherSunny
                          className={cc(!d.data.hasMorning && classes.noTimes)}
                          style={{ visibility: isSelected && !d.data.hasMorning ? 'hidden' : undefined }}
                        />
                      </Grid>
                      <Grid item xs={12} sm>
                        <ClockOutline
                          className={cc(!d.data.hasAfternoon && classes.noTimes)}
                          style={{ visibility: isSelected && !d.data.hasAfternoon ? 'hidden' : undefined }}
                        />
                      </Grid>
                      <Grid item xs={12} sm>
                        <WeatherNight
                          className={cc(!d.data.hasEvening && classes.noTimes)}
                          style={{ visibility: isSelected && !d.data.hasEvening ? 'hidden' : undefined }}
                        />
                      </Grid>
                    </Grid>
                  }
                </ButtonBase>
              </Grid>;
            })}
          </Grid>
        </Collapse>
      </Grid>;
    })}
    { (isLoading || hasNoCalendarData) &&
      <div className={classes.spinnerOverlay}>
        { isLoading && <Spinner text="Loading..." /> }
        { hasNoCalendarData &&
          <Paper elavation={2} className={classes.warningOverlay}>
            <Alert />&nbsp;&nbsp;There are no appointments available this month
          </Paper>
        }
      </div>
    }
  </Grid>;
});

const useTimesStyles = makeStyles(theme => ({
  timesHeader: {
    textAlign: 'center'
  }
}));

const CalendarTimes = observer(function CalendarTimes({ store }) {
  const classes = useTimesStyles();
  const { selectedDay, isLoadingTimes } = store;

  const handleBack = useCallback(() => store.selectDay(null), [ store ]);

  if (selectedDay && isLoadingTimes) {
    return <Spinner text="Loading..." />;
  }

  return <Fragment>
    <Grid item>
      <Grid container spacing={2}>
        <Grid item xs={12} sm>
          <div className={classes.timesHeader}><WeatherSunny />: Morning Times (Before 12pm)</div>
          { selectedDay && <CalendarTimesList filter={0} store={store} /> }
        </Grid>
        <Grid item xs={12} sm>
          <div className={classes.timesHeader}><ClockOutline />: Afternoon Times (12pm - 5pm)</div>
          { selectedDay && <CalendarTimesList filter={1} store={store} /> }
        </Grid>
        <Grid item xs={12} sm>
          <div className={classes.timesHeader}><WeatherNight />: Evening Times (After 5pm)</div>
          { selectedDay && <CalendarTimesList filter={2} store={store} /> }
        </Grid>
      </Grid>
    </Grid>
    { selectedDay &&
      <Grid item>
        <Grid container>
          <Grid item xs />
          <Grid item>
            <Button color="primary" onClick={handleBack} startIcon={<ChevronLeft />}>Back</Button>
          </Grid>
        </Grid>
      </Grid>
    }
  </Fragment>;
});

const CalendarTimesList = observer(function CalendarTimesList({ filter, store }) {
  const { times } = store;
  const filtered = times.filter(t => t.timeOfDay === filter);

  return <Paper style={{ marginTop: 16 }}>
    <List disablePadding>
      { filtered.map((item, i) => <CalendarTimesItems key={i} store={store} item={item} divider={i < filtered.length - 1} />)}
      { !filtered.length &&
        <ListItem>
          <ListItemText primary="No times available" />
        </ListItem>
      }
    </List>
  </Paper>;
});

const CalendarTimesItems = observer(function CalendarTimesItems({ store, item, divider }) {
  const { time, start, end, groupId, currentGroupSize, totalGroupSize } = item;
  const handleTime = useCallback(() => store.selectTime(time, groupId), [ store, time, groupId ]);
  const primary = `${shortTime(start)} - ${shortTime(end)}`;
  let secondary = groupId ? 'Group Appointment' : null;
  if (secondary && currentGroupSize != null) {
    secondary += `: ${currentGroupSize}${totalGroupSize == null ? '' : ('/' + totalGroupSize)} other participants`;
  }

  return <ListItem button divider={divider} onClick={handleTime}>
    <ListItemText primary={primary} secondary={secondary} />
    <ChevronRight />
  </ListItem>;
});